import React, { useMemo, useState, useCallback } from "react";
import { NavLink } from "react-router-dom";
import { translate } from "../../../config/translator";
import { connector } from "./redux";
import {TabletPage, TabletContainer } from "../../components/Layout/TabletLayout";
import { TwoColumnGrid, MainColumn, RightRail } from "../../components/Layout/TwoColumn";
import {
  isContentManager,
  isInsider,
  isRegistered,
  isTeamInsider,
  isPremium,
} from "../../../config/utils";
import LeftSide from "./LeftSide";
import ClavaButton from "../../components/ClavaButton";
import RoundedRectangle from "../../components/RoundedRectangle";
import SegmentedTabs from "../../components/SegmentedTabs";
import PlayerImage from "../../components/PlayerImage";
import { BETA_ENDPOINT, DEV_ENDPOINT, PROD_ENDPOINT, STAG_ENDPOINT, RECAPTCHA_ACTION_LOGIN_IDENTITY } from "../../../config/constants";
import client from "../../../client";
import { DropdownItem } from "../../components/ClavaDropdown";
import {LanguageLocaleEnum, User} from "../../../client/api";
import { useReCaptcha } from "../../../hooks/useReCaptcha";

const endpoints = {
  Production: PROD_ENDPOINT,
  Stag: STAG_ENDPOINT,
  Test: BETA_ENDPOINT,
  Development: DEV_ENDPOINT,
};
const endpointItems: DropdownItem<keyof typeof endpoints>[] = [
  { label: "Production", key: "Production" },
  { label: "Stag", key: "Stag" },
  { label: "Test", key: "Test" },
  { label: "Development", key: "Development" },
];

type Props = {
  changeUsernameStatus: string;
  logout: () => void;
  deleteAccount: () => void;
  setEndpoint: (endpoint: string) => void;
  user: User | null;
  l: LanguageLocaleEnum;
  confirmTel: (code: string) => void;
};

function isEndpointKey(v: string): v is keyof typeof endpoints {
  return v === "Production" || v === "Stag" || v === "Test" || v === "Development";
}

const Profile: React.FC<Props> = ({
  changeUsernameStatus,
  logout,
  deleteAccount,
  setEndpoint,
  user,
  l,
  confirmTel,
}) => {
  const premium = useMemo(() => isPremium(user), [user]);
  const insider = useMemo(() => isInsider(user, undefined), [user]);
  const teamInsider = useMemo(() => isTeamInsider(user), [user]);
  const [username] = useState<string>(user?.username ?? "");
  const registered = useMemo(() => isRegistered(user), [user]);
  const [deleteAccountModal, setDeleteAccount] = useState<boolean>(false);
  const [telCode, setTelCode] = useState<string>("");
  const { reCaptchaToken, refresh } = useReCaptcha(RECAPTCHA_ACTION_LOGIN_IDENTITY);
  const captchaTokenRef = React.useRef<string | undefined>(undefined);
  React.useEffect(() => { captchaTokenRef.current = reCaptchaToken; }, [reCaptchaToken]);
  const [secondsLeftEmail, setSecondsLeftEmail] = useState<number>(0);
  const [emailCodeSent, setEmailCodeSent] = useState<boolean>(false);
  const [emailSending, setEmailSending] = useState<boolean>(false);
  const [telSecondsLeft, setTelSecondsLeft] = useState<number>(0);
  const [telSending, setTelSending] = useState<boolean>(false);
  const canResendTel = useMemo(() => telSecondsLeft <= 0 && reCaptchaToken !== undefined, [telSecondsLeft, reCaptchaToken]);
  React.useEffect(() => {
    const t = window.setInterval(() => setTelSecondsLeft((s) => (s > 0 ? s - 1 : s)), 1000);
    return () => window.clearInterval(t);
  }, []);
  const canResendEmail = useMemo(() => secondsLeftEmail <= 0 && reCaptchaToken !== undefined, [secondsLeftEmail, reCaptchaToken]);
  React.useEffect(() => {
    const t = window.setInterval(() => setSecondsLeftEmail((s) => (s > 0 ? s - 1 : s)), 1000);
    return () => window.clearInterval(t);
  }, []);
  const [selectedEndpoint, setSelectedEndpoint] = useState<
    keyof typeof endpoints
  >(
    client().getEndpoint() === PROD_ENDPOINT
      ? "Production"
      : client().getEndpoint() === STAG_ENDPOINT
      ? "Stag"
      : client().getEndpoint() === BETA_ENDPOINT
      ? "Test"
      : "Development"
  );
  const onDeleteAccount = useCallback(() => {
    setDeleteAccount(true);
  }, []);
  const onCancelDeleteAccount = useCallback(() => {
    setDeleteAccount(false);
  }, []);
  const onChangeEndpoint = useCallback(
    (item: keyof typeof endpoints | undefined) => {
      if (item) {
        setSelectedEndpoint(item);
        if (item in endpoints) {
          const endpoint = endpoints[item];
          setEndpoint(endpoint);
        }
      }
    },
    [setEndpoint]
  );
  return (
    <TabletPage>
      <TabletContainer className="pb-6 gap-6">
        <TwoColumnGrid>
          <MainColumn>
            <RoundedRectangle
              className="bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark mb-4 "
              hasBorder
            >
              <div className="flex flex-col items-center justify-start p-6 space-y-6">
                <div className="w-full flex flex-row flex-wrap items-center justify-center gap-2">
                  {teamInsider && (
                    <SegmentedTabs
                      className="p-0 gap-2"
                      size="sm"
                      items={[{ key: "teamInsider", label: translate("teamInsider", l), disabled: true }]}
                      activeKey="teamInsider"
                    />
                  )}
                  {!teamInsider && insider && (
                    <SegmentedTabs
                      className="p-0 gap-2"
                      size="sm"
                      items={[{ key: "insider", label: translate("insider", l), disabled: true }]}
                      activeKey="insider"
                    />
                  )}
                  {premium && (
                    <SegmentedTabs
                      className="p-0 gap-2"
                      size="sm"
                      items={[{ key: "premium", label: translate("premium", l), disabled: true }]}
                      activeKey="premium"
                    />
                  )}
                </div>

                <PlayerImage image={user?.photo ?? null} width={128} className="rounded-full overflow-hidden" round />

                <div className="text-center items-center w-full">
                  {changeUsernameStatus === "usernameGiven" && (
                    <span className="text-red mb-2 block">
                      {translate(changeUsernameStatus, l)}
                    </span>
                  )}
                  <div className="w-full flex justify-center">
                    <SegmentedTabs
                      className="p-0 gap-2"
                      variant="primary"
                      activeKey="username"
                      items={[{
                        key: "username",
                        label: username || translate("username", l),
                        disabled: true,
                      }]}
                    />
                  </div>
                </div>

                {isContentManager(user) && (
                  <div className="w-full max-w-xs space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-font-600 dark:text-font-600-dark mb-2">{translate("server", l)}</label>
                      <select
                        value={selectedEndpoint}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (isEndpointKey(v)) onChangeEndpoint(v);
                        }}
                        className="w-full rounded-md border border-light-gray dark:border-light-gray-dark bg-bg-secondary dark:bg-bg-secondary-dark p-2 text-font dark:text-font-dark"
                      >
                        {endpointItems.map((opt) => (
                          <option key={String(opt.key)} value={String(opt.key)}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div className="flex flex-col items-center justify-start space-y-3 w-full max-w-xs">
                  {!(user && user.emailConfirmed) && (
                    <div className="w-full flex flex-col items-center space-y-2">
                      {!(user && user.email) && (
                        <div className="w-full flex justify-center">
                          <SegmentedTabs
                            className="p-0 gap-2"
                            items={[{
                              key: "register",
                              label: translate("register", l),
                              onClick: () => { window.location.href = "/register"; },
                            }]}
                          />
                        </div>
                      )}
                      {!registered && (
                        <NavLink
                          to="/login"
                          className="block text-decoration-underline text-muted text-xs mt-2"
                        >
                          <span>{`${translate("alreadyRegistered", l)} ${translate("login", l)}`}</span>
                        </NavLink>
                      )}
                    </div>
                  )}

                  {registered && (
                    <div className="w-full space-y-3">
                      {isContentManager(user) && (
                        <div className="w-full flex justify-center">
                          <SegmentedTabs
                            className="p-0 gap-2"
                            variant="primary"
                            items={[{
                              key: "admin",
                              label: translate("adminpanel", l),
                              onClick: () => { window.location.href = "https://admin.clava-sports.com/"; },
                            }]}
                          />
                        </div>
                      )}
                      <div className="w-full flex flex-col items-center space-y-3">
                        <div className="w-full flex justify-center">
                          <SegmentedTabs
                            className="p-0 gap-2"
                            items={[{
                              key: "logout",
                              label: translate("logout", l),
                              onClick: logout,
                            }]}
                          />
                        </div>
                        <div className="w-full flex justify-center">
                          <SegmentedTabs
                            className="p-0 gap-2"
                            items={[{
                              key: "deleteAccount",
                              label: translate("deleteAccount", l),
                              onClick: onDeleteAccount,
                            }]}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                </div>
              </div>
            </RoundedRectangle>
          </MainColumn>

          <RightRail className="gap-4">
            {user && user.email && !user.emailConfirmed && (
              <RoundedRectangle
                className="bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark "
                hasBorder
              >
                <div className="p-4">
                  <div className="mb-2 font-semibold">{translate("confirmMail", l)}</div>
                  <p className="text-xs mb-3">{translate(emailCodeSent ? "confirmMailInfoLink" : "confirmMailInviteLink", l)}</p>
                  <div className="w-full flex justify-center">
                    <SegmentedTabs
                      className="p-0 gap-2"
                      variant={emailCodeSent ? "default" : "primary"}
                      items={[{
                        key: emailCodeSent ? "resendEmail" : "sendEmail",
                        label: emailCodeSent
                          ? (canResendEmail ? translate("resendCode", l) : `${translate("resendAvailableIn", l)} ${Math.max(0, secondsLeftEmail)}s`)
                          : translate("sendCode", l),
                        onClick: () => {
                          if (emailSending) return;
                          setEmailSending(true);
                          const attemptSend = () => {
                            const tok = captchaTokenRef.current;
                            if (!tok) {
                              setTimeout(attemptSend, 200);
                              return;
                            }
                            client().sendConfirmMail(tok).then(() => {
                              setEmailCodeSent(true);
                              setSecondsLeftEmail(60);
                              setEmailSending(false);
                            }).catch(() => {
                              setEmailSending(false);
                            });
                          };
                          refresh();
                          setTimeout(attemptSend, 650);
                        },
                      }]}
                    />
                  </div>
                </div>
              </RoundedRectangle>
            )}
            {user && user.tel && !user.telConfirmed && (
              <RoundedRectangle
                className="bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark "
                hasBorder
              >
                <div className="p-4">
                  <div className="mb-2 font-semibold">{translate("confirmTel", l)}</div>
                  <p className="text-xs mb-3">{translate("confirmTelInfoCode", l)}</p>
                  <div className="my-2">
                    <input
                      value={telCode}
                      onChange={(e) => setTelCode(e.target.value)}
                      placeholder={translate("code", l)}
                      className="w-full rounded-md border border-light-gray dark:border-light-gray-dark bg-bg-secondary dark:bg-bg-secondary-dark p-2 text-font dark:text-font-dark"
                    />
                  </div>
                  <div className="w-full flex justify-center gap-4">
                    <SegmentedTabs
                      className="p-0 gap-2"
                      variant="primary"
                      items={[{
                        key: "submitTel",
                        label: translate("submit", l),
                        onClick: () => { if (telCode.length) confirmTel(telCode); },
                      }]}
                      activeKey={telCode.length ? "submitTel" : undefined}
                    />
                    <SegmentedTabs
                      className="p-0 gap-2"
                      items={[{
                        key: "resendTel",
                        label: (canResendTel ? translate("resendCode", l) : `${translate("resendAvailableIn", l)} ${Math.max(0, telSecondsLeft)}s`),
                        onClick: () => {
                          if (telSending) return;
                          setTelSending(true);
                          refresh();
                          setTimeout(() => {
                            const tok = captchaTokenRef.current;
                            if (!tok) { setTelSending(false); return; }
                            client().resendCode(tok).then(() => {
                              setTelSecondsLeft(60);
                              setTelSending(false);
                            }).catch(() => {
                              setTelSending(false);
                            });
                          }, 650);
                        },
                      }]}
                      activeKey={canResendTel ? "resendTel" : undefined}
                    />
                  </div>
                </div>
              </RoundedRectangle>
            )}
            <LeftSide />
          </RightRail>
        </TwoColumnGrid>

        {deleteAccountModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay-50 dark:bg-overlay-50-dark">
            <div className="w-full max-w-lg rounded-lg bg-bg-secondary dark:bg-bg-secondary-dark p-6 shadow">
              <h3 className="text-sm font-semibold text-font dark:text-font-dark mb-2">{translate("deleteAccount", l)}</h3>
              <p className="text-font-600 dark:text-font-600-dark mb-4">{translate("deleteAccountCont", l)}</p>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                <ClavaButton onClick={deleteAccount} secondary>
                  {translate("yes", l)}
                </ClavaButton>
                <ClavaButton onClick={onCancelDeleteAccount}>
                  {translate("no", l)}
                </ClavaButton>
              </div>
            </div>
          </div>
        )}
      </TabletContainer>
    </TabletPage>
  );
};

export default connector(Profile);
